import ApiService from './ApiService'

// ----- Types based on your updated Spring Boot DTOs -----

export type ProductItemResponse = {
  id: string
  productId: string
  priceId: string
  name: string
  description: string
  amount: number
  currency: string
  credits: number
  popular: boolean
  type: 'SUBSCRIPTION' | 'ONE_TIME'
  interval?: string  // Optional as it's only present for subscriptions
}

export type ProductsResponse = {
  productType: 'SUBSCRIPTION' | 'ONE_TIME'
  products: ProductItemResponse[]
}

// ----- Product Endpoints -----

/**
 * Get products based on user's subscription status.
 * If user does not have an active subscription, it returns subscription products.
 * If user has an active subscription, it returns one-time purchase products.
 * Endpoint: GET /api/products
 */
export async function apiGetProducts<T = ProductsResponse>() {
  return ApiService.fetchData<T>({
    url: '/api/products',
    method: 'get',
  })
}

/**
 * Get subscription products.
 * Available for all pages, secured endpoint or not.
 * Endpoint: GET /api/products/subscriptions
 */
export async function apiGetSubscriptions<T = ProductsResponse>() {
  return ApiService.fetchData<T>({
    url: '/api/subscriptions',
    method: 'get',
  })
}

/**
 * Get one-time purchase products.
 * Only available if the user HAS an active subscription.
 * Endpoint: GET /api/products/purchases
 */
export async function apiGetPurchaseProducts<T>() {
  return ApiService.fetchData<T>({
    url: '/api/products/purchases',
    method: 'get',
  })
}

// ----- Subscription Endpoints -----

/**
 * Create a checkout session for subscription purchase.
 * Endpoint: GET /api/subscription/checkout-session?priceId=xxx
 */
export async function apiGetSubscriptionCheckoutSession<T>(priceId: string) {
  return ApiService.fetchData<T>({
    url: `/api/stripe/subscription/checkout-session?priceId=${priceId}`,
    method: 'get',
  })
}

/**
 * Get the customer portal URL for subscription management.
 * Endpoint: GET /api/subscription/portal
 */
export async function apiGetCustomerPortalUrl<T>() {
  return ApiService.fetchData<T>({
    url: `/api/stripe/portal`,
    method: 'get',
  })
}


// ----- Payment Endpoints -----

/**
 * Create a checkout session for payment events.
 * Endpoint: GET /api/checkout-session?priceId=xxx
 */
export async function apiGetPaymentCheckoutSession<T>(priceId: string) {
  return ApiService.fetchData<T>({
    url: `/api/stripe/payment/checkout-session?priceId=${priceId}`,
    method: 'get',
  })
}
